import Icon from '@/components/icon';
import { IconType } from '@/components/icon/helpers';
import Text from '@/components/text';
import { alpha, Box, Stack, Theme } from '@mui/material';
import { FC, useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import SearchInput from './search-input';
import { debounce } from 'lodash';
import { UiOption } from '@/lib/helpers';
import QuickFilterButton from '../quick-filter-button';
import TileGroupMenu from './menu';
import useThemeMediaQuery from '@/hooks/use-theme-media-query';
import SelectMulti from '../form/select-multi';

interface Props {
  title: string;
  icon: IconType;
  onSearch?: (value: string) => void;
  onSort?: () => void;
  sorting?: boolean;
  onChangeQuickFilters?: (value: string, enabled: boolean) => void;
  quickFilters?: string[];
  quickFilterOptions?: UiOption[];
  border?: boolean;
}

const useStyles = createUseStyles<string, { border: boolean }>((theme: Theme) => ({
  tileGroupHeader: {
    height: 36,
    paddingBottom: 4,
    marginBottom: 8,
    borderBottom: ({ border }) =>
      border ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
  },
  quickFilterSelect: {
    '& .MuiFormControl-root': {
      padding: 0,
      margin: 0,
    },
  },
}));

const TileGroupHeader: FC<Props> = ({
  title,
  icon,
  onSearch,
  onSort,
  quickFilters = [],
  quickFilterOptions = [],
  onChangeQuickFilters,
  border = true,
}) => {
  const [keyword, setKeyword] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const { xsmall, small } = useThemeMediaQuery();

  const styles = useStyles({ border });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceHandleSearch = useCallback(
    debounce((value: string) => {
      onSearch?.(value);
    }, 200),
    []
  );

  const handleSearch = (value: string) => {
    setKeyword(value);
    debounceHandleSearch(value);
  };

  const handleSort = () => {
    onSort?.();
  };

  const handleChangeQuickFilter = (value: string, enabled: boolean) => {
    onChangeQuickFilters?.(value, enabled);
  };

  const hasSearch = !!onSearch;
  const hasSort = !!onSort;
  const hasQuickFilters = !!quickFilterOptions.length;
  const showQuickFilters = hasQuickFilters && (!small || (small && !searchOpen));

  return (
    <Stack
      gap={1}
      direction="row"
      alignItems="center"
      justifyContent="flex-start"
      width="100%"
      className={styles.tileGroupHeader}
    >
      <Icon name={icon} size="small" />
      <Text bold size="small" noWrap>
        {title}
      </Text>
      <Stack direction="row" flexGrow={1} justifyContent="flex-end" gap={1} alignItems="center">
        {hasSearch && (
          <SearchInput value={keyword} onChange={handleSearch} onChangeOpen={setSearchOpen} />
        )}
        {hasSort && <TileGroupMenu onSort={handleSort} />}
        {showQuickFilters && !small && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={0.5}
            flexWrap="wrap"
          >
            {quickFilterOptions.map(({ label, value }, index) => {
              return (
                <QuickFilterButton
                  key={index}
                  label={label as string}
                  enabled={quickFilters.includes(value)}
                  onChange={(enabled) => handleChangeQuickFilter?.(value, enabled)}
                />
              );
            })}
          </Stack>
        )}

        {showQuickFilters && small && (
          <Box width={xsmall ? '60%' : '50%'} className={styles.quickFilterSelect}>
            <SelectMulti
              name="quick-filters"
              options={quickFilterOptions}
              value={quickFilters}
              size="x-small"
              placeholder="Categories"
              onChange={(_options, change: { value: string; enabled: boolean }) => {
                handleChangeQuickFilter(change.value, change.enabled);
              }}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default TileGroupHeader;
